// 카카오 오픈채팅 입장 코드 — 화면이 서버를 부르는 곳과 화면 문구(원본 한 곳) · 2026-10-29
// 기획 docs/ops/openchat-entry-code-plan.md · 마이그레이션 20261213100000 · 시험 scripts/sql-tests/openchat-entry-code/
//
// 🔒 규칙
//   · 시행은 2026-11-01 0시(KST) — 서버(app_config.openchat_room.from)가 판정한다. 화면은 날짜를 따로 계산하지 않는다.
//   · 닉네임은 **입장 코드만**(제이 2026-10-29). 「닉네임 복사」도 코드만 복사한다.
//   · 판정(내보내기·확인 필요)은 서버 admin_openchat_judge 한 곳 — 화면은 결과만 정렬해 보여 준다.
//   · 어드민이 다른 회원으로 보고 있을 때는 그 회원 이메일을 넘긴다(넘기지 않으면 어드민 자기 것이 보인다).
import { supabase } from "./supabaseClient";

export const OPENCHAT_RPC = {
  visible: "openchat_room_visible",
  mine: "my_openchat_codes",
  add: "openchat_add_team_code",
  revoke: "openchat_revoke_code",
  rotate: "openchat_rotate_owner_code",
  judge: "admin_openchat_judge",
  saveCheck: "admin_openchat_save_check",
  find: "admin_openchat_find_code",
  adminRevoke: "admin_openchat_revoke_code",
  saveRoom: "admin_openchat_save_room",
  overview: "admin_openchat_overview",
};

export const OPENCHAT_ROOM_NAME = "[공지방] 레이블리 - 유튜브";
export const OPENCHAT_MAX_MEMBERS = 5;
export const OPENCHAT_LABEL_MAX = 40;

/** 닉네임 = 코드만 */
export function nicknameFor(code) {
  return String(code || "").trim();
}

/** 팀원에게 보낼 문장 — 링크·참여코드는 넣지 않는다(팀원은 같은 계정으로 이 페이지에서 본다) */
export function teamMessageFor(code) {
  return `레이블리 오픈채팅 닉네임은 입장 코드 ${nicknameFor(code)} 만 써 주세요. 이름·채널명 등 다른 글자는 넣지 마세요.`;
}

/** 방 공지 문구(어드민 「복사」) — 첫 줄은 「공지방 이전」 */
export function roomNoticeText(roomName = OPENCHAT_ROOM_NAME) {
  return [
    `${roomName} — 오픈채팅으로 이전한 방이에요.`,
    "글은 운영진만 쓸 수 있어요.",
    "닉네임에는 레이블리에서 받은 입장 코드만 써 주세요.",
    "예) 7K2Q-A3",
    "이름·채널명 등 다른 글자는 넣지 마세요.",
    "코드는 레이블리 ≡ 메뉴 「오픈채팅 입장」에 있어요.",
    "닉네임이 입장 코드와 다르면 매일 확인 때 안내 없이 내보내져요.",
  ].join("\n");
}

/** 붙여 넣은 글을 한 줄에 한 명으로 — 빈 줄은 뺀다 */
export function parseNicknames(text) {
  return String(text || "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
}

export const VERDICT_LABEL = {
  staff: "운영진",
  ok: "정상",
  fix: "다른 글자 붙음",
  duplicate: "같은 코드 여러 명",
  no_code: "코드 없음",
  unknown: "없는 코드",
  revoked: "삭제된 코드",
  inactive: "대상 아님",
};

export const VERDICT_HELP = {
  fix: "닉네임에 코드 말고 다른 글자가 붙어 있어요 — 안내 없이 내보내요",
  duplicate: "같은 코드를 쓰는 사람이 둘 이상이에요",
  no_code: "닉네임에 입장 코드가 없어요",
  unknown: "레이블리가 준 적 없는 코드예요",
  revoked: "크리에이터가 지웠거나 바꾸기 전의 옛 코드예요",
  inactive: "코드 주인이 탈퇴했거나 승인된 채널이 없어요",
};

export const TODO_LABEL = {
  kick: "내보내기",
  ask: "크리에이터에게 확인",
  none: "없음",
};

const TODO_RANK = { kick: 0, ask: 1, none: 2 };

/** 내보낼 사람 → 확인 → 그대로 순. 같은 묶음 안에서는 붙여 넣은 순서 */
export function sortJudged(rows) {
  return (rows || [])
    .map((r, i) => ({ ...r, _i: i }))
    .sort((a, b) => (TODO_RANK[a.todo] ?? 9) - (TODO_RANK[b.todo] ?? 9) || a._i - b._i)
    .map(({ _i, ...r }) => r);
}

export function countTodos(rows) {
  const out = { kick: 0, ask: 0, none: 0 };
  (rows || []).forEach((r) => { if (out[r.todo] != null) out[r.todo] += 1; });
  return out;
}

const ERROR_TEXT = {
  openchat_team_full: "팀원 코드는 5개까지예요. 안 쓰는 코드를 지우고 다시 만들어 주세요.",
  openchat_rate_limited: "오늘은 코드를 너무 많이 만들었어요. 내일 다시 해 주세요.",
  openchat_bad_label: "메모는 40자까지예요.",
  openchat_not_available: "지금은 입장 코드를 만들 수 없어요.",
  openchat_code_not_found: "이미 지운 코드예요. 화면을 새로 고쳐 주세요.",
  openchat_bad_link: "오픈채팅 링크는 https://open.kakao.com/ 으로 시작해야 해요.",
  openchat_bad_join_code: "참여코드는 40자까지예요.",
  forbidden: "이 화면을 볼 수 없는 계정이에요.",
};

/** 서버 오류 → 위 표의 열쇠(크리에이터 화면은 이 열쇠로 번역 문장을 고른다). 모르는 오류는 null */
export function openchatErrorKey(error) {
  const msg = String(error?.message || error || "");
  return Object.keys(ERROR_TEXT).find((k) => msg.includes(k)) || null;
}

/** 서버 오류를 화면 문장으로 — 모르는 오류는 원문 대신 일반 문장 */
export function openchatErrorText(error) {
  const msg = String(error?.message || error || "");
  const key = Object.keys(ERROR_TEXT).find((k) => msg.includes(k));
  return key ? ERROR_TEXT[key] : "잠시 뒤 다시 해 주세요.";
}

async function call(name, args) {
  const { data, error } = await supabase.rpc(name, args);
  if (error) throw error;
  return data;
}

let visibleCache = null;
/** 메뉴 노출 — 서버가 시행 시각·스위치를 본다(어드민은 미리보기로 참). 실패하면 숨김 */
export async function fetchOpenchatVisible({ force = false } = {}) {
  if (visibleCache !== null && !force) return visibleCache;
  try {
    visibleCache = (await call(OPENCHAT_RPC.visible)) === true;
  } catch {
    visibleCache = false;
  }
  return visibleCache;
}
export function getOpenchatVisibleCache() { return visibleCache; }
/** 테스트용 */
export function resetOpenchatVisibleCache() { visibleCache = null; }

export const fetchMyOpenchat = (email) => call(OPENCHAT_RPC.mine, { p_email: email });
export const addTeamCode = (email, label) => call(OPENCHAT_RPC.add, { p_email: email, p_label: label || null });
export const revokeTeamCode = (email, codeId) => call(OPENCHAT_RPC.revoke, { p_email: email, p_code_id: codeId });
export const rotateOwnerCode = (email) => call(OPENCHAT_RPC.rotate, { p_email: email });

export const adminJudge = (nicknames) => call(OPENCHAT_RPC.judge, { p_nicknames: nicknames });
export const adminSaveCheck = (memberCount, result, kickedCount) =>
  call(OPENCHAT_RPC.saveCheck, { p_member_count: memberCount, p_result: result, p_kicked_count: kickedCount });
export const adminFindCode = (code) => call(OPENCHAT_RPC.find, { p_code: code });
export const adminRevokeCode = (codeId) => call(OPENCHAT_RPC.adminRevoke, { p_code_id: codeId });
export const adminSaveRoom = ({ enabled, link, joinCode, staffNicknames }) =>
  call(OPENCHAT_RPC.saveRoom, { p_enabled: enabled, p_link: link, p_join_code: joinCode, p_staff_nicknames: staffNicknames });
export const adminOverview = () => call(OPENCHAT_RPC.overview);
